import React from 'react';
import { matchPath, useHistory } from 'react-router-dom';

import { Result } from 'api/companies/public/paginate/params';

import { CONFIG } from 'constants/index';
import { useAnalytics } from 'client/hooks';
import { Scroller } from 'client/utils/scroller';
import { useAppSelector } from 'client/hooks/redux';
import { selectUserGAStatus } from 'client/redux/user/selectors';
import { companies as companiesApi } from 'client/redux/api/companies';
import { categories as categoriesApi } from 'client/redux/api/categories';
import { ScreenSlider } from 'client/components/common/mobile/ScreenSlider';
import { selectLang } from 'client/redux/settings/selectors';
import { MAIN_PAGE_ROUTE } from 'client/constants/routes';

import { Company } from './Company';

import css from './Companies.module.scss';

interface Props {
  scroller: Scroller;
  className?: string;
  containerRef?: React.RefObject<HTMLDivElement>;
  active?: boolean;
  onDeactivate: () => unknown;
}

const FETCH_SIZE = 100;
const DEFAULT_COMPANIES: Result['items'] = [];

const Companies = (props: Props) => {
  const { scroller, active: isActive = false, className = '', onDeactivate } = props;
  const history = useHistory();
  const analytics = useAnalytics();
  const userGAStatus = useAppSelector(selectUserGAStatus);
  const isMainPageRoute = Boolean(matchPath(history.location.pathname, { path: MAIN_PAGE_ROUTE, exact: true }));

  const urlParams = new URLSearchParams(history.location.search);
  const search = urlParams.get('search') || '';
  const category = urlParams.get('category') || '';
  const lang = useAppSelector(selectLang);

  // Fetch companies:
  const [offset, setOffset] = React.useState(0);
  const [paginate, { data: paginateRes, isFetching: isPaginateFetching, isLoading: isPaginateLoading }] =
    companiesApi.endpoints.paginateCompanies.useLazyQuery();
  const [
    paginateByCategories,
    {
      data: paginateByCategoriesRes,
      isFetching: isPaginateByCategoriesFetching,
      isLoading: isPaginateByCategriesLoading,
    },
  ] = companiesApi.endpoints.getCompanyWithCategoryOrder.useLazyQuery();

  const { data, isLoading: isCategoriesLoading } = categoriesApi.endpoints.getAllCategories.useQuery({
    isVisible: true,
  });
  const categories = data?.success ? data.data : [];

  React.useEffect(() => {
    setOffset(0);

    if (!category) {
      paginate({ search, offset, limit: FETCH_SIZE });
    } else {
      paginateByCategories({ search, filter: { categoryId: category }, offset, limit: FETCH_SIZE });
    }
  }, [search, category, offset, paginate, paginateByCategories]);

  React.useEffect(() => {
    const page = urlParams.get('page') || '';

    if (userGAStatus) {
      if (!category && !isCategoriesLoading && isMainPageRoute) {
        analytics.gtag.event('page_view', {
          page_title: 'MainPage',
          page_location: page
            ? `${CONFIG.domainUrl}${MAIN_PAGE_ROUTE.replace(':lang', lang)}?${urlParams}`
            : `${CONFIG.domainUrl}${MAIN_PAGE_ROUTE.replace(':lang', lang)}`,
        });
      }

      if (category && !isCategoriesLoading) {
        const categoryData = categories?.find((item) => item._id.toString() === category);

        analytics.gtag.event('page_view', {
          page_title: `Category - ${categoryData?.name}`,
          page_location: `${CONFIG.domainUrl}${MAIN_PAGE_ROUTE.replace(':lang', lang)}?${urlParams}`,
        });
      }
    }
  }, [history.location, isCategoriesLoading, userGAStatus]);

  const paginateCompanies = paginateRes?.success ? paginateRes.data.items : DEFAULT_COMPANIES;
  const paginateByCategoriesCompanies = paginateByCategoriesRes?.success
    ? paginateByCategoriesRes.data.items
    : DEFAULT_COMPANIES;
  const companies = category ? paginateByCategoriesCompanies : paginateCompanies;
  const paginateTotal = (paginateRes?.success ? paginateRes.data.page.total : 0) ?? 0;
  const paginateByCategoriesTotal =
    (paginateByCategoriesRes?.success ? paginateByCategoriesRes.data.page.total : 0) ?? 0;
  const total = category ? paginateByCategoriesTotal : paginateTotal;
  const isBusy = category
    ? isPaginateByCategoriesFetching || isPaginateByCategriesLoading
    : isPaginateFetching || isPaginateLoading;

  const page = Number(urlParams.get('page')) || 1;
  const onSliderPageChange = React.useCallback(
    (page: number) => {
      const urlParams = new URLSearchParams(history.location.search);

      analytics.gtag.event('MobileSwipe', {
        PageID: String(page),
      });

      urlParams.set('page', String(page));
      history.push(`${MAIN_PAGE_ROUTE.replace(':lang', lang)}?${urlParams.toString()}`);
    },
    [history]
  );
  const onSliderDeactivate = React.useCallback(() => {
    const urlParams = new URLSearchParams(history.location.search);

    urlParams.delete('page');
    history.push(`${MAIN_PAGE_ROUTE.replace(':lang', lang)}?${urlParams.toString()}`);

    onDeactivate();
  }, [history, onDeactivate]);

  // TODO: TRANSLATIONS
  const NoResults = React.useMemo(
    () =>
      function NoResults() {
        if (category && !search) {
          return (
            <div className={css.noResults}>
              <p className={css.noResultsText}>
                No companies are listed in this category yet, please check back later.
              </p>
            </div>
          );
        }
        // TODO: TRANSLATIONS
        return (
          <div className={css.noResults}>
            <div className={css.searchValue}>{`“${search.toUpperCase()}”`}</div>
            <p className={css.noResultsText}>
              Your search for <strong>{`“${search}”`}</strong> didn&apos;t return any results
            </p>
          </div>
        );
      },
    [category, search]
  );

  return (
    <ScreenSlider
      className={className}
      items={companies}
      page={page}
      total={total}
      scroller={scroller}
      active={isActive}
      busy={isBusy}
      destroyOnReachingTop
      containerRef={props.containerRef}
      slide={Company}
      noResults={NoResults}
      onPageChange={onSliderPageChange}
      onDeactivate={onSliderDeactivate}
    />
  );
};

export default Companies;
